/**
 * ChartGuide — a "?" icon button that opens a popover explaining how to read a chart.
 *
 * Drop it anywhere in a card header Box, usually right-aligned via `ml: 'auto'` or
 * placed after a ToggleButtonGroup. It renders nothing but the icon; all content
 * lives inside the popover.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PROPS — use only the ones that apply to your chart. All are optional.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * methodology?: string
 *   HOW the numbers are calculated — the "what counts" rule.
 *   Use this whenever a metric could be misread without knowing its definition.
 *
 *   Good methodology notes answer questions like:
 *   - Is this a count of events, users, or completions?
 *   - Does it deduplicate? (e.g. "each user counted once even if they completed
 *     multiple courses")
 *   - What qualifies? (e.g. "only users who hold an active RN license at any rank")
 *   - What is excluded? (e.g. "test accounts and staff completions are excluded")
 *
 *   Data engineering rule of thumb: if two people could read the chart title and
 *   arrive at different denominator assumptions, you need a methodology note.
 *
 *   Example:
 *     methodology="Counts distinct users who earned at least one certificate
 *                  (event or course) in the period. A user who completed three
 *                  courses in a week counts as one. Staff accounts excluded."
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * organization?: string
 *   HOW time buckets are defined — "what does one bar/point represent?"
 *   Use this whenever the bucket boundary is non-obvious or affects interpretation.
 *
 *   There are two common patterns:
 *
 *   • Calendar buckets — boundaries are fixed (Jan 1–Dec 31, first of month,
 *     Monday–Sunday ISO week). The current bucket is always incomplete until
 *     the period ends. A spike or dip at the right edge of the chart is normal —
 *     it just means the period hasn't finished yet.
 *     Example: "Each bar is a calendar year (Jan 1–Dec 31). The rightmost bar
 *              is the current year and will grow until Dec 31."
 *
 *   • Rolling windows — the bucket trails back a fixed duration from today
 *     (e.g. last 30 days, last 52 weeks). Every bucket is always "complete"
 *     in the sense that 30 days have elapsed, but the window shifts daily,
 *     so yesterday's "last 30 days" bar and today's are slightly different.
 *     Example: "Each point represents a rolling 30-day window ending on that date."
 *
 *   Calendar buckets are better for year-over-year comparison.
 *   Rolling windows are better for spotting trends in recent activity.
 *   If you're unsure which the backend uses, check the API contract — look for
 *   fields named windowStart (rolling) vs. year/month/isoWeek (calendar).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * usage?: string
 *   HOW to interact with the chart — controls, toggles, drill-downs.
 *   Use this when the chart has non-obvious interactive elements, or when
 *   switching views changes the interpretation (not just the resolution).
 *
 *   Good usage notes tell the reader:
 *   - What each toggle/filter does
 *   - Whether switching views resets any state
 *   - What the reader should look for at each granularity
 *
 *   Granularity guidance for three-level toggles (Year / Month / Week):
 *   - Year: best for long-run growth trends and YoY comparisons. Smooths over
 *     seasonal variation. Use this first to establish the baseline story.
 *   - Month: reveals seasonality and campaign effects. A bump in a single month
 *     that looks flat at year level becomes visible here.
 *   - Week: highest resolution. Good for spotting a specific launch, outage, or
 *     content release. Noisy — week-to-week variance is normal and expected;
 *     look for multi-week sustained changes, not single-week spikes.
 *
 *   Example:
 *     usage="Toggle Year / Month / Week to change time resolution.
 *            Year is best for trend direction; Week is best for pinpointing
 *            specific events. The current period is always incomplete."
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * USAGE EXAMPLE:
 *
 *   import { ChartGuide } from 'src/v3/components/charts/chart-guide'
 *
 *   <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1.5 }}>
 *     <Typography variant="h6">My Chart</Typography>
 *     <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
 *       <ToggleButtonGroup ... />
 *       <ChartGuide
 *         methodology="Counts distinct users who completed at least one course."
 *         organization="Calendar year — Jan 1 through Dec 31. Current year is incomplete."
 *         usage="Toggle Year / Month / Week to change resolution."
 *       />
 *     </Box>
 *   </Box>
 */

import { ReactElement, useState } from 'react'
import { Box, IconButton, Popover, Typography } from '@mui/material'
import { InfoIcon } from 'src/v3/core/icons'

type ChartGuideProps = {
  methodology?: string
  organization?: string
  usage?: string
}

type Section = {
  label: string
  body: string
}

export let ChartGuide = ({
  methodology,
  organization,
  usage,
}: ChartGuideProps): ReactElement | null => {
  let [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)

  let sections: Section[] = [
    ...(methodology ? [{ label: 'Methodology', body: methodology }] : []),
    ...(organization ? [{ label: 'Organization', body: organization }] : []),
    ...(usage ? [{ label: 'Usage', body: usage }] : []),
  ]

  if (sections.length === 0) return null

  return (
    <>
      <IconButton
        size="small"
        aria-label="How to read this chart"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ color: 'text.secondary' }}
      >
        <InfoIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2.5, maxWidth: 340 }}>
          {sections.map(({ label, body }, i) => (
            <Box key={label} sx={{ mb: i < sections.length - 1 ? 2 : 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
                color="text.secondary"
              >
                {label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}
